<template>
  <div class="tournament-branding">
    <div class="section-title-row">
      <v-icon small class="mr-2">fa-regular fa-palette</v-icon>
      <span class="section-title">{{ $t("appearance") }}</span>
    </div>
    <div class="branding-layout">
      <!-- LEFT -->
      <div class="info-cards">
        <!-- LIGHT CARD -->
        <div class="info-card info-card--light">
          <div class="section-title-row-card">
            <div class="section-title">{{ $t("light_mode") }}</div>
            <v-icon small class="mr-2">fa-regular fa-sun</v-icon>
          </div>

          <hr class="section-divider" />

          <!-- Appearance (Light only) -->
          <div class="mode-card light">
            <div class="color-row" @click="openColorPicker('backgroundLight')">
              <div class="color-name">{{ $t("selectBackgroundColor") }}</div>
              <div class="left-color">
                <div class="color-hex">{{ colors.backgroundLight }}</div>
                <div class="color-chip" :style="{ backgroundColor: colors.backgroundLight }"></div>
              </div>
            </div>

            <div class="color-row" @click="openColorPicker('primaryLight')">
              <div class="color-name">{{ $t("primaryColor") }}</div>
              <div class="left-color">
                <div class="color-hex">{{ colors.primaryLight }}</div>
                <div class="color-chip" :style="{ backgroundColor: colors.primaryLight }" ></div>
              </div>
            </div>
            <!-- Contrast indicators for Light mode -->
            <div class="contrast-row">
              <div class="contrast-label">
                {{$t('Background') }} {{$t('primaryColor') }} <br>
                <b>{{ backgroundPrimaryLightContrastMet ? $t('contrastStandardMet') : $t('contrastStandardNotMet') }}</b>
              </div>
              <div class="contrast-indicator">
                <v-icon v-if="backgroundPrimaryLightContrastMet" small class="check-icon met">fa-solid fa-check</v-icon>
                <v-icon v-else small class="check-icon not-met">fa-solid fa-xmark</v-icon>
                <div class="color-circle">
                  <div class="color-half primary" :style="{ '--dynamic-bg': colors.backgroundLight  }"></div>
                  <div class="color-half secondary" :style="{ '--dynamic-bg': colors.primaryLight }"></div>
                </div>
              </div>
            </div> 

            <hr class="section-divider"/>

            <div class="color-row" @click="openColorPicker('secondaryLight')">
              <div class="color-name">{{ $t("secondaryColor") }}</div>
              <div class="left-color">
                <div class="color-hex">{{ colors.secondaryLight }}</div>
                <div class="color-chip" :style="{ backgroundColor: colors.secondaryLight }"></div>
              </div>             
            </div>

            <!-- Contrast indicators for Light mode -->
            <div class="contrast-row">
              <div class="contrast-label">
                {{$t('primaryColor') }} {{$t('secondaryColor') }} <br>
                <b>{{ PrimarySecondaryLightContrastMet ? $t('contrastStandardMet') : $t('contrastStandardNotMet') }}</b>
              </div>
              <div class="contrast-indicator">
                <v-icon v-if="PrimarySecondaryLightContrastMet" small class="check-icon met">fa-solid fa-check</v-icon>
                <v-icon v-else small class="check-icon not-met">fa-solid fa-xmark</v-icon>
                <div class="color-circle">
                  <div class="color-half primary" :style="{ '--dynamic-bg': colors.primaryLight }"></div>
                  <div class="color-half secondary" :style="{ '--dynamic-bg': colors.secondaryLight }"></div>
                </div>
              </div>
            </div> 
          </div>
          <hr class="section-divider"/>

          <!-- Partner Logo (Light) -->
           <div class="title-logo">{{ $t('logo') }}</div>

           <div class="upload-dropzone">
            <div
              class="upload-box"
              :class="{ 'upload-box--has-image': imagePreviewLight,  dragging: isDraggingLight }"
              @dragover.prevent
              @dragenter.prevent="onDragEnter('LIGHT')"
              @dragleave.prevent="onDragLeave('LIGHT')"
              @drop.prevent="onDrop($event, 'LIGHT')"
            >
              <template v-if="imagePreviewLight">
                <div class="upload-box__previewRow">
                  <img :src="imagePreviewLight" alt="Preview" class="background-icon" />
                  <div class="upload-box__fileInfo">
                    <div class="upload-box__title"> {{ $t("uploadImage") }} </div>
                    <div class="upload-box__meta"> {{ $t('rules.type') }}: PNG, JPG  </div>
                    <ImageInput  v-model="partnerLogoLight"  @input="(v) => handleImageChange(v, 'LIGHT')" >
                      <div slot="activator">
                        <button type="button" class="upload-box__changeLink" >{{ $t("browseFilesButton") }}  <i class="fa-regular fa-file-arrow-up"></i>  </button>
                      </div>
                    </ImageInput>
                  </div>
                  <button type="button" class="upload-box__trashBtn" @click.stop="removeImage('LIGHT')">
                    <v-icon size="18">fa-regular fa-trash-can</v-icon>
                  </button>
                </div>
              </template>

              <template v-else>
                <div class="upload-box__left">
                  <div class="background-icon"> <v-icon class="upload-box__icon" small> fa-regular fa-cloud-arrow-up </v-icon>   </div>
                  <div class="upload-box__text">
                    <div class="upload-box__title">  {{ $t("uploadImage") }}  </div>
                    <div class="upload-box__meta">  {{ $t('size') }}: 2MB  </div>
                    <div class="upload-box__meta"> {{ $t('rules.type') }}: PNG, JPG  </div>
                  </div>
                </div>
                <div class="upload-box__right">
                  <ImageInput  v-model="partnerLogoLight"   @input="(v) => handleImageChange(v, 'LIGHT')" >
                    <div slot="activator">
                      <v-btn depressed class="upload-box__btn">  <v-icon left small>fa-regular fa-folder-open</v-icon> {{ $t("browseFilesButton") }} </v-btn>
                    </div>
                  </ImageInput>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- DARK CARD -->
        <div class="info-card info-card--dark">
          <div class="section-title-row-card dark">
            <div class="section-title">{{ $t("dark_mode") }}</div>
            <v-icon small class="mr-2 icon-color">fa-regular fa-moon</v-icon>
          </div>
          <hr class="section-divider" />
          <div class="mode-card dark">
            <div class="color-row" @click="openColorPicker('backgroundDark')">
              <div class="color-name">{{ $t("selectBackgroundColor") }}</div>
              <div class="left-color">
                <div class="color-hex">{{ colors.backgroundDark }}</div>
                <div class="color-chip" :style="{ backgroundColor: colors.backgroundDark }"></div>
              </div>
            </div>

            <div class="color-row" @click="openColorPicker('primaryDark')">
              <div class="color-name">{{ $t("primaryColor") }}</div>
              <div class="left-color">
                <div class="color-hex">{{ colors.primaryDark }}</div>
                <div class="color-chip" :style="{ backgroundColor: colors.primaryDark }" ></div>
              </div>
            </div>
            <!-- Contrast indicators for dark mode -->
            <div class="contrast-row">
              <div class="contrast-label">
                {{$t('Background') }} {{$t('primaryColor') }} <br>
                <b>{{ backgroundPrimaryDarkContrastMet ? $t('contrastStandardMet') : $t('contrastStandardNotMet') }}</b>
              </div>
              <div class="contrast-indicator">
                <v-icon v-if="backgroundPrimaryDarkContrastMet" small class="check-icon met">fa-solid fa-check</v-icon>
                <v-icon v-else small class="check-icon not-met">fa-solid fa-xmark</v-icon>
                <div class="color-circle">
                  <div class="color-half primary" :style="{ '--dynamic-bg': colors.backgroundDark  }"></div>
                  <div class="color-half secondary" :style="{ '--dynamic-bg': colors.primaryDark }"></div>
                </div>
              </div>
            </div> 

            <hr class="section-divider"/>

            <div class="color-row" @click="openColorPicker('secondaryDark')">
              <div class="color-name">{{ $t("secondaryColor") }}</div>
              <div class="left-color">
                <div class="color-hex">{{ colors.secondaryDark }}</div>
                <div class="color-chip" :style="{ backgroundColor: colors.secondaryDark }"></div>
              </div>             
            </div>

            <!-- Contrast indicators for dark mode -->
            <div class="contrast-row">
              <div class="contrast-label">
                {{$t('primaryColor') }} {{$t('secondaryColor') }} <br>
                <b>{{ PrimarySecondaryDarkContrastMet ? $t('contrastStandardMet') : $t('contrastStandardNotMet') }}</b>
              </div>
              <div class="contrast-indicator">
                <v-icon v-if="PrimarySecondaryDarkContrastMet" small class="check-icon met">fa-solid fa-check</v-icon>
                <v-icon v-else small class="check-icon not-met">fa-solid fa-xmark</v-icon>
                <div class="color-circle">
                  <div class="color-half primary" :style="{ '--dynamic-bg': colors.primaryDark }"></div>
                  <div class="color-half secondary" :style="{ '--dynamic-bg': colors.secondaryDark }"></div>
                </div>
              </div>
            </div> 
          </div>
          <hr class="section-divider"/>

          <!-- Partner Logo (dark) -->
           <div class="title-logo">{{ $t('logo') }}</div>

           <div class="upload-dropzone">
            <div
              class="upload-box"
              :class="{ 'upload-box--has-image': imagePreviewDark,  dragging: isDraggingDark }"
              @dragover.prevent
              @dragenter.prevent="onDragEnter('DARK')"
              @dragleave.prevent="onDragLeave('DARK')"
              @drop.prevent="onDrop($event, 'DARK')"
            >
              <template v-if="imagePreviewDark">
                <div class="upload-box__previewRow">
                  <img :src="imagePreviewDark" alt="Preview" class="background-icon" />
                  <div class="upload-box__fileInfo">
                    <div class="upload-box__title"> {{ $t("uploadImage") }} </div>
                    <div class="upload-box__meta"> {{ $t('rules.type') }}: PNG, JPG  </div>
                    <ImageInput  v-model="partnerLogoDark"  @input="(v) => handleImageChange(v, 'DARK')" >
                      <div slot="activator">
                        <button type="button" class="upload-box__changeLink" >{{ $t("browseFilesButton") }}  <i class="fa-regular fa-file-arrow-up"></i>  </button>
                      </div>
                    </ImageInput>
                  </div>
                  <button type="button" class="upload-box__trashBtn--dark" @click.stop="removeImage('DARK')">
                    <v-icon size="18">fa-regular fa-trash-can</v-icon>
                  </button>
                </div>
              </template>

              <template v-else>
                <div class="upload-box__left">
                  <div class="background-icon"> <v-icon class="upload-box__icon" small> fa-regular fa-cloud-arrow-up </v-icon>   </div>
                  <div class="upload-box__text">
                    <div class="upload-box__title">  {{ $t("uploadImage") }}  </div>
                    <div class="upload-box__meta">  {{ $t('size') }}: 2MB  </div>
                    <div class="upload-box__meta"> {{ $t('rules.type') }}: PNG, JPG  </div>
                  </div>
                </div>
                <div class="upload-box__right">
                  <ImageInput  v-model="partnerLogoDark"   @input="(v) => handleImageChange(v, 'DARK')" >
                    <div slot="activator">
                      <v-btn depressed class="upload-box__btn">  <v-icon left small>fa-regular fa-folder-open</v-icon> {{ $t("browseFilesButton") }} </v-btn>
                    </div>
                  </ImageInput>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT -->
      <div class="preview-card">
        <div class="preview-header">
          <div class="preview-title">{{ $t("rankingLayout_livePreview") }}</div>
          <div class="preview-subtitle">
            {{ $t("rankingLayout_descrition1") }}
          </div>
        </div>

          <div v-if="previewMode === 'LIGHT'" class="preview-block light" :style="{ backgroundColor: colors.backgroundLight }">

            <div class="preview-top">
              <v-icon small class="mr-2">fa-regular fa-palette</v-icon>
              <span>{{ $t("appearance") }}</span>
              <v-spacer />
              <span class="mode-caption">{{ $t("seeing_dark") }}</span>
              <button class="mode-toggle"  type="button"  @click="togglePreviewMode" >
                <v-icon small>fa-regular fa-moon</v-icon>
              </button>
            </div>

            <div>
              <div class="logo-preview-wrapper">
                <div class="logo-preview-container">
                  <v-img  v-if="imagePreviewLight"   :src="imagePreviewLight"   alt="Logo preview" class="logo-preview-image"  contain />
                </div>
              </div>
              <div class="sample">              
                <div class="sample-header" :style="{ backgroundColor: colors.primaryLight, color: colors.secondaryLight, '--text-color': colors.secondaryLight, }" > {{ $t("tournamentHeaderSample") }} </div>
                <div class="sample-content light-preview">
                  <button class="sample-btn" :style="{ backgroundColor: colors.primaryLight, color: colors.secondaryLight, '--text-color': colors.secondaryLight, }" > {{ $t("primaryButtonSample") }} </button>
                  <div class="sample-text" :style="{ color: colors.secondaryLight,  '--text-color': colors.secondaryLight,  }" >  {{ $t("colorPreviewDescription") }}  </div>
                </div>
              </div>
            </div>

            <div class="sample-text">{{ $t('contrastHelperText') }}</div>

          </div>

          <div v-else class="preview-block dark" :style="{ backgroundColor: colors.backgroundDark }">

            <div class="preview-top">
              <v-icon small class="mr-2">fa-regular fa-palette</v-icon>
              <span>{{ $t("appearance") }}</span>
              <v-spacer />
              <span class="mode-caption">{{ $t("seeing_light") }}</span>
              <button class="mode-toggle"  type="button" @click="togglePreviewMode" >
                <v-icon small>fa-regular fa-sun</v-icon>
              </button>
            </div>

            <div>
              <div class="logo-preview-wrapper">
                <div class="logo-preview-container">
                  <v-img  v-if="imagePreviewDark"   :src="imagePreviewDark"   alt="Logo preview" class="logo-preview-image"  contain />
                </div>
              </div>
              <div class="sample dark">
              <div  class="sample-header" :style="{  backgroundColor: colors.primaryDark, color: colors.secondaryDark,  '--text-color': colors.secondaryDark,  }" > {{ $t("tournamentHeaderSample") }} </div>
              <div class="sample-content dark">
                <button class="sample-btn" :style="{  backgroundColor: colors.primaryDark,  color: colors.secondaryDark,  '--text-color': colors.secondaryDark,  }" >  {{ $t("primaryButtonSample") }} </button>
                <div class="sample-text" :style="{  color: colors.secondaryDark,   '--text-color': colors.secondaryDark,  }" >  {{ $t("colorPreviewDescription") }} </div>
              </div>
            </div>
          </div>
                  
            <div class="sample-text">{{ $t('contrastHelperText') }}</div>

          </div>
      </div>
    </div>


    <v-overlay :dark="false" v-model="showColorPicker">
      <CustomColorPicker
        :key="selectedColorType"
        :initialColor="selectedColorValue"
        :pickerTitle="colorPickerTitle"
        :modeLabel="pickerModeLabel"
        :suggestedTitle="$t('SuggestedColors')"
        :cancelText="$t('btnCancel')"
        :confirmText="$t('continue')"
        :themeMode="pickerThemeMode"
        :pickerVariant="pickerVariant"
        :suggestedColors="pickerSuggestedColors"
        @saveColor="saveColor"
        @closeWindow="showColorPicker = false"
      />
    </v-overlay>
  </div>
</template>

<script>
import ColorPicker from "@/components/ColorPicker.vue";
import CustomColorPicker from "@/components/CustomColorPicker.vue";
import ImageInput from "@/components/ImageInput.vue";
import Repository from "../../../../repositories/tournament";
import { mapMutations } from "vuex";
// import CustomColorPicker from "@/components/CustomColorPicker.vue";

export default {
  name: "TournamentBranding",
  components: { ColorPicker, CustomColorPicker, ImageInput },
  props: {
    stepActive: { type: Boolean, required: false },
    setNextBtnDisabled: { type: Function, required: false },
  },
  computed: {
    pickerVariant() {
      return this.selectedColorType && this.selectedColorType.toLowerCase().includes("background") ? "background" : "color";},

    pickerThemeMode() {
      return this.selectedColorType && this.selectedColorType.toLowerCase().includes("dark") ? "dark" : "light";
    },

    pickerModeLabel() {
      return this.pickerThemeMode === "dark" ? this.$t("dark_mode") : this.$t("light_mode"); },

    pickerSuggestedColors() {
      if (this.pickerThemeMode === "dark") {
        return ["#5E5F61", "#4B4D53", "#393C43", "#262830", "#12141A"];
      }
      return ["#FFFFFF", "#F9F9F9", "#F4F4F4", "#EEEEEE", "#E8E8E8"];
    },
    tournamentSummary() {
      return this.$store.state.tournament;
    },

    backgroundPrimaryLightContrastMet() {
      return this.checkContrastRatio(  this.colors.backgroundLight,  this.colors.primaryLight );
    },

    PrimarySecondaryLightContrastMet(){
      return this.checkContrastRatio( this.colors.primaryLight,  this.colors.secondaryLight );
    },

    backgroundPrimaryDarkContrastMet(){
      return this.checkContrastRatio( this.colors.backgroundDark, this.colors.primaryDark  );
    },

    PrimarySecondaryDarkContrastMet(){
      return this.checkContrastRatio( this.colors.primaryDark, this.colors.secondaryDark );
    },

    colorPickerTitle() {
      if (!this.selectedColorType) return this.$t("selectColor");
      if (this.selectedColorType.toLowerCase().includes("background")) {
        return this.$t("selectBackgroundColor");
      }
      if (this.selectedColorType.toLowerCase().includes("primary")) {
        return this.$t("primaryColor");
      }
      if (this.selectedColorType.toLowerCase().includes("secondary")) {
        return this.$t("secondaryColor");
      }
      return this.$t("selectColor");
    },
  },
  data: () => ({
    partnerLogoLight: null,
    partnerLogoDark: null,
    imagePreviewLight: null,
    imagePreviewDark: null,
    previewMode: "LIGHT",

    colors: {
      backgroundLight: "#FFFFFF",
      backgroundDark: "#000000",
      primaryLight: "#2F80ED",
      primaryDark: "#1F6FE5",
      secondaryLight: "#333333",
      secondaryDark: "#FFFFFF",
    },

    showColorPicker: false,
    selectedColorType: null,
    selectedColorValue: "",

    initialValues: null,
    uploadingImage: false,
    imageUploaded: false,

    isDraggingLight: false,
    isDraggingDark: false,
    _dragCounterLight: 0,
    _dragCounterDark: 0,
  }),
  mounted() {
    if (this.tournamentSummary) {
      this.init();
    }
    // Disable save button initially
    if (this.setNextBtnDisabled) {
      this.setNextBtnDisabled(true);
    }
  },
  watch: {
    tournamentSummary: {
      handler(newVal) {
        if (newVal && !this.initialValues) {
          this.init();
        }
      },
      immediate: true,
    },
    imagePreviewLight() {
      this.checkForChanges();
    },
    imagePreviewDark() {
      this.checkForChanges();
    },
    colors: {
      handler() {
        this.checkForChanges();
      },
      deep: true,
    },
  },
  methods: {
    ...mapMutations(["setTournament"]),
    withHash(hex) {
      if (!hex) return hex;
      return hex.startsWith("#") ? hex : `#${hex}`;
    },

    // WCAG Contrast Ratio calculation methods
    hexToRgb(hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
          }
        : null;
    },

    getLuminance(r, g, b) {
      // Convert RGB to sRGB
      const [rs, gs, bs] = [r, g, b].map((c) => {
        c = c / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
      });
      // Calculate relative luminance
      return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
    },

    getContrastRatio(color1, color2) {
      const rgb1 = this.hexToRgb(color1);
      const rgb2 = this.hexToRgb(color2);

      if (!rgb1 || !rgb2) return 1;

      const l1 = this.getLuminance(rgb1.r, rgb1.g, rgb1.b);
      const l2 = this.getLuminance(rgb2.r, rgb2.g, rgb2.b);

      const lighter = Math.max(l1, l2);
      const darker = Math.min(l1, l2);

      return (lighter + 0.05) / (darker + 0.05);
    },

    checkContrastRatio(OneColor, TwoColor) {
      const ratio = this.getContrastRatio(OneColor, TwoColor);
      // WCAG AA requires 4.5:1 for normal text, 3:1 for large text
      // We use 4.5:1 as the standard for AA compliance
      return ratio >= 4.5;
    },

    onDragEnter(mode) {
      if (mode === "LIGHT") {
        this._dragCounterLight += 1;
        this.isDraggingLight = true;
      } else {
        this._dragCounterDark += 1;
        this.isDraggingDark = true;
      }
    },

    onDragLeave(mode) {
      if (mode === "LIGHT") {
        this._dragCounterLight -= 1;
        if (this._dragCounterLight <= 0) {
          this.isDraggingLight = false;
          this._dragCounterLight = 0;
        }
      } else {
        this._dragCounterDark -= 1;
        if (this._dragCounterDark <= 0) {
          this.isDraggingDark = false;
          this._dragCounterDark = 0;
        }
      }
    },

    onDrop(e, mode) {
      if (mode === "LIGHT") {
        this._dragCounterLight = 0;
        this.isDraggingLight = false;
      } else {
        this._dragCounterDark = 0;
        this.isDraggingDark = false;
      }

      const file = e.dataTransfer?.files?.[0];
      if (!file) return;

      const allowed = ["image/jpeg", "image/png", "image/svg+xml"];
      const maxBytes = 1 * 1024 * 1024;

      if (!allowed.includes(file.type)) {
        this.$emit("save-error", { message: "Only JPG/PNG/SVG are allowed." });
        return;
      }
      if (file.size > maxBytes) {
        this.$emit("save-error", { message: "Max file size is 2MB." });
        return;
      }

      const imageURL = URL.createObjectURL(file);
      const formData = new FormData();
      formData.append("file", file);

      const payload = { imageURL, formData, file };
      
      if (mode === "LIGHT") {
        this.partnerLogoLight = payload;
      } else {
        this.partnerLogoDark = payload;
      }
 
      this.handleImageChange(payload, mode);
    },

    async init() {
      if (!this.tournamentSummary?.tournamentId) return;
      try {
        this._isInitializing = true;
        this.imagePreviewLight = null;
        this.imagePreviewDark = null;

        const res = await Repository.getTournamentBranding(
          this.tournamentSummary.tournamentId,
        );
        const branding = res?.data || {};

        this.imagePreviewLight = branding.light?.fullLogo || null;
        this.imagePreviewDark = branding.dark?.fullLogo || null;

        if (branding.light) {
          this.colors.backgroundLight = this.withHash(branding.light.background) || this.colors.backgroundLight
          this.colors.primaryLight = this.withHash(branding.light.brandPrimary) || this.colors.primaryLight;
          this.colors.secondaryLight = this.withHash(branding.light.brandSecondary) || this.colors.secondaryLight;
        }

        if (branding.dark) {
          this.colors.backgroundDark = this.withHash(branding.dark.background) || this.colors.backgroundDark
          this.colors.primaryDark = this.withHash(branding.dark.brandPrimary) || this.colors.primaryDark;
          this.colors.secondaryDark = this.withHash(branding.dark.brandSecondary) || this.colors.secondaryDark;
        }

        this.saveInitialValues();
        this.checkForChanges();
      } catch (e) {
        console.error("Error loading tournament branding:", e);
        this.saveInitialValues();
      } finally {
        this._isInitializing = false;
      }
    },

    saveInitialValues() {
      this.initialValues = {
        imagePreviewLight: this.imagePreviewLight || null,
        imagePreviewDark: this.imagePreviewDark || null,
        backgroundLight: this.colors.backgroundLight,
        primaryLight: this.colors.primaryLight,
        secondaryLight: this.colors.secondaryLight,
        backgroundDark: this.colors.backgroundDark,
        primaryDark: this.colors.primaryDark,
        secondaryDark: this.colors.secondaryDark,
      };
    },

    checkForChanges() {
      if (!this.initialValues) { this.setNextBtnDisabled?.(true); return; }
      const hasChanges =
        this.imagePreviewLight !== this.initialValues.imagePreviewLight ||
        this.imagePreviewDark !== this.initialValues.imagePreviewDark ||
        this.colors.backgroundLight !== this.initialValues.backgroundLight ||
        this.colors.primaryLight !== this.initialValues.primaryLight ||
        this.colors.secondaryLight !== this.initialValues.secondaryLight ||
        this.colors.primaryDark !== this.initialValues.primaryDark ||
        this.colors.secondaryDark !== this.initialValues.secondaryDark ||
        this.colors.backgroundDark !== this.initialValues.backgroundDark;
      this.setNextBtnDisabled?.(!hasChanges);
    },

    togglePreviewMode() {
      this.previewMode = this.previewMode === "LIGHT" ? "DARK" : "LIGHT";
    },

    handleImageChange(imageData, mode) {
      const file = imageData?.file || imageData?.formData?.get?.("file");
      if (file) {
        const allowed = ["image/jpeg", "image/png", "image/svg+xml"];
        const maxBytes = 1 * 1024 * 1024;

        if (!allowed.includes(file.type)) {
          this.$emit("save-error", {
            message: "Only JPG/PNG/SVG are allowed.",
          });
          return;
        }
        if (file.size > maxBytes) {
          this.$emit("save-error", { message: "Max file size is 1MB." });
          return;
        }
      }
      if (mode === "LIGHT") {
        this.partnerLogoLight = imageData;
        if (imageData?.imageURL) {
          this.imagePreviewLight = imageData.imageURL;
        }
      }

      if (mode === "DARK") {
        this.partnerLogoDark = imageData;
        if (imageData?.imageURL) {
          this.imagePreviewDark = imageData.imageURL;
        }
      }

      this.checkForChanges();
    },

    removeImage(mode) {
      if (mode === "LIGHT") {
        this.partnerLogoLight = null;
        this.imagePreviewLight = null;
      } else {
        this.partnerLogoDark = null;
        this.imagePreviewDark = null;
      }
      this.imageUploaded = false;
      this.checkForChanges();
    },

    openColorPicker(type) {
      this.selectedColorType = type;
      this.selectedColorValue = this.colors[type] || "#009F63";
      this.showColorPicker = true;
    },

    saveColor(color) {
      if (!this.selectedColorType) return;
      this.$set(this.colors, this.selectedColorType, color);
      this.showColorPicker = false;
    },

    saveTournamentSettings() {
      // Validate that tournament exists in store
      if (!this.tournamentSummary || !this.tournamentSummary.tournamentId) {
        this.$emit("save-error", { message: this.$t("alerts.errorOccurred") || "Tournament data not available", }); return;
      }
      this.saveTournamentBranding();
    },

    saveTournamentBranding() {
      const formData = new FormData();

      // --- Helpers ---
      const extractFile = (img) => {
        if (!img) return null;
        if (img.file instanceof Blob) return img.file; 
        const fdFile = img.formData?.get?.("file");
        if (fdFile instanceof Blob) return fdFile;
        return null;
      };

      const toNamedFile = (file, fallbackName) => {
        if (!file) return null;
        if (file instanceof File && file.name) return file;
        if (file instanceof Blob) {
          return new File([file], fallbackName, {
            type: file.type || "application/octet-stream",
          });
        }

        return null;
      };

      // LIGHT
      formData.append("light.type", "LIGHT");
      formData.append("light.background", this.colors.backgroundLight.replace("#", ""), );
      formData.append("light.brandPrimary", this.colors.primaryLight.replace("#", ""), );
      formData.append( "light.brandSecondary", this.colors.secondaryLight.replace("#", ""), );

      const rawLight = extractFile(this.partnerLogoLight);
      const lightFile = toNamedFile(rawLight, "light-logo.png");
      if (lightFile) { formData.append("light.image", lightFile, lightFile.name); }

      // DARK
      formData.append("dark.type", "DARK");
      formData.append("dark.background", this.colors.backgroundDark.replace("#", ""), );
      formData.append("dark.brandPrimary", this.colors.primaryDark.replace("#", ""), );
      formData.append("dark.brandSecondary", this.colors.secondaryDark.replace("#", ""), );

      const rawDark = extractFile(this.partnerLogoDark);
      const darkFile = toNamedFile(rawDark, "dark-logo.png");
        if (darkFile) { formData.append("dark.image", darkFile, darkFile.name); }

      for (const [k, v] of formData.entries()) {
        if (v instanceof File) console.log(k, v.name, v.type, v.size);
        else console.log(k, v);
      }

      // ========================
      // CALL BULK ENDPOINT
      // ========================
      Repository.editTournamentBrandingBulk( this.tournamentSummary.tournamentId, formData, )
        .then((response) => {
          if (response.status < 400) {
            this.init(); 
            this.setNextBtnDisabled?.(true);
            this.$emit("save-success", { message: "Branding updated successfully", });
          } else {
            this.$emit("save-error", { message: "Error updating branding", });
          }
        })
        .catch((error) => {
          this.$emit("save-error", { message: error.response?.data?.message || "Error updating branding", });
        });
    },

    handleSaveColor(color) {
      this.primaryColor = color;
      this.showColorPicker = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.tournament-branding {
  width: 100%;
  font-family: "Avenir", sans-serif;

  .title {
    font-size: 32px !important;
    font-weight: bold;
    margin-bottom: 8px;
    color: var(--v-textPrimary-base);
  }
  .subtitle {
    font-size: 16px;
    color: var(--v-textSecondary-base);
    margin-bottom: 24px;
  }
}

.branding-layout {
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 24px;
  align-items: stretch;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
}

.info-card {
  background-color: var(--v-cardBackground-base);
  border: 1px solid #ededed;
  border-radius: 10px;
  padding: 10px;
  position: relative;
}
.theme--dark .info-card {
  border-color: #424242;
}

.section {
  margin-bottom: 22px;
}

.section-title-row {
  display: flex;
  align-items: center;
  margin-bottom: 12px;

  .section-title {
    font-weight: 600;
  }
}

.section-title-row-card {
  display: flex;
  justify-content: space-between;
  margin-bottom: 30px;
}

.theme--dark .section-title-row-card .v-icon {
  color: var(--v-fullWhite-base) !important;
}

.theme--dark .section-title-row .section-title {
  color: var(--v-fullWhite-base) !important;
}

.theme--dark .section-title-row .v-icon {
  color: var(--v-fullWhite-base) !important;
}

.title-logo{
  margin-top: 20px;
  margin-bottom: 20px;
}

.upload-dropzone {
  border: 1px solid #ABABAB;
  border-radius: 10px;
  padding: 20px 12px;
}

.upload-dropzone-dark {
  border: 1px solid #ABABAB;
  border-radius: 10px;
  padding: 16px;
  transition: all 0.3s ease;
}

.theme--dark .upload-dropzone {
  border-color: #424242;
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
}

.theme--dark .upload-empty {
  color: var(--v-mainWhite);
}

.theme--dark .upload-title {
  color: var(--v-mainWhite);
}

.theme--dark .upload-subtitle {
  color: rgba(255, 255, 255, 0.7);
}

.theme--dark .upload-hint {
  color: rgba(255, 255, 255, 0.6);
}

.theme--dark .upload-empty .v-icon {
  color: rgba(255, 255, 255, 0.7);
}

.preview-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.preview-img {
  border-radius: 8px;
  border: 1px solid #e6e6e6;
}

.theme--dark .preview-img {
  border-color: #9a9a9a;
}

.browse-btn {
  background: var(--v-mainGreen-base) !important;
  color: white !important;
  border-radius: 8px !important;
  height: 36px;
}

.appearance-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.mode-card {
  .mode-title {
    font-weight: 600;
    font-size: 12px;
    margin-bottom: 10px;
    opacity: 0.9;
    color: var(--v-mainBlack-base);
  }

  &.dark {
    border-color: #fff;
  }
}

.theme--dark .mode-card {
  border-color: #424242;
  background: var(--v-cardBackground-base);
}

.theme--dark .mode-card .mode-title {
  color: var(--v-fullWhite-base) !important;
  opacity: 1 !important;
  font-weight: 600;
}

.theme--dark .mode-card .color-name {
  color: var(--v-fullWhite-base) !important;
  font-weight: 600;
}

.theme--dark .mode-card .color-hex {
  color: rgba(255, 255, 255, 0.8) !important;
}

.color-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  margin: 14px 0;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  .color-chip {
    width: 26px;
    height: 26px;
    border-radius: 6px;
    border: 1px solid rgba(0, 0, 0, 0.1);
  }

  .color-name {
    font-size: 14px;
    font-weight: 600;
    color: #ABABAB;
  }

  .color-hex {
    font-size: 14px;
    color: #ABABAB;
  }
}

.left-color {
  display: flex;
  gap: 10px;
  align-items: center;
}

.mode-card.dark {
  .color-hex {
    color: rgba(255, 255, 255, 0.8) !important;
    opacity: 1 !important;
  }
}

.theme--dark .color-row:hover {
  background: rgba(255, 255, 255, 0.08);
}

.preview-card {
  height: 100%;
  min-height: 640px;
  display: flex;
  flex-direction: column;
  border: 1px solid #e6e6e6;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;

  .preview-header {
    padding: 18px;
    text-align: center;
    border-bottom: 1px solid #eee;

    .preview-title {
      font-family: "Aeonik", "Avenir", sans-serif !important;
      font-size: 30px;
      font-weight: 800;
      color: var(--v-textPrimary-base);
    }
    .preview-subtitle {
      font-size: 12px;
      color: var(--v-textSecondary-base);
    }
  }

}

.theme--dark .preview-card {
  border-color: #424242;
  background: var(--v-cardBackground-base);
}

.theme--dark .preview-card .preview-header {
  border-bottom-color: #424242;
}

.theme--dark .preview-card .preview-title {
  color: var(--v-fullWhite-base) !important;
  font-weight: 700;
}

.theme--dark .preview-card .preview-subtitle {
  color: rgba(255, 255, 255, 0.8) !important;
}

.preview-block {
  height: 100%;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  &.dark {
    color: #fff;
    border-color: #3a424d;

    .preview-top {
      color: #fff !important;
      opacity: 1 !important;

      .v-icon {
        color: #fff !important;
        opacity: 1 !important;
      }

      span {
        color: #fff !important;
        opacity: 1 !important;
      }
    }

    .mode-caption {
      color: #fff !important;
      opacity: 1 !important;
    }
  }

  .preview-top {
    display: flex;
    align-items: center;
    font-size: 12px;
    opacity: 0.9;
    margin-bottom: 10px;
    color: var(--v-mainBlack-base);

    > span {
      color: var(--v-mainBlack-base);
    }
  }

  .mode-caption {
    font-size: 11px;
    opacity: 0.7;
    color: var(--v-mainBlack-base);
  }
}

.theme--dark .preview-block {
  border-color: #424242;
}

.theme--dark .preview-block .preview-top {
  color: var(--v-fullWhite-base) !important;
  opacity: 1 !important;
}

.theme--dark .preview-block .mode-caption {
  color: var(--v-fullWhite-base) !important;
  opacity: 1 !important;
}

.theme--dark .preview-block .preview-top .v-icon {
  color: var(--v-fullWhite-base) !important;
  opacity: 1 !important;
}

.theme--dark .preview-block .preview-top span {
  color: var(--v-fullWhite-base) !important;
  opacity: 1 !important;
}

.preview-block.dark .preview-top {
  color: #fff !important;
  opacity: 1 !important;
}

.preview-block.dark .preview-top .v-icon {
  color: #fff !important;
  opacity: 1 !important;
}

.preview-block.dark .preview-top > span:not(.mode-caption) {
  color: #fff !important;
  opacity: 1 !important;
  -webkit-text-fill-color: #fff !important;
}

.preview-block.dark .preview-top span.mode-caption {
  color: #fff !important;
  opacity: 1 !important;
  -webkit-text-fill-color: #fff !important;
}

.preview-block.dark .mode-caption {
  color: #fff !important;
  opacity: 1 !important;
  -webkit-text-fill-color: #fff !important;
}

.mode-toggle {
  border: 0;
  background: transparent;
  cursor: pointer;
  padding-left: 10px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }

  .v-icon {
    color: var(--v-mainBlack-base);
  }
}

.theme--dark .mode-toggle {
  .v-icon {
    color: var(--v-fullWhite-base) !important;
    opacity: 1 !important;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
}

.preview-block.dark .mode-toggle {
  .v-icon {
    color: #fff !important;
    opacity: 1 !important;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
}

.preview-block.dark {
  .mode-toggle:hover {
    background: rgba(255, 255, 255, 0.1);
  }
}

.sample {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #eee;

  .sample-header {
    padding: 10px;
    font-weight: 600;
    text-align: center;
    font-size: 12px;
  }

  .sample-content {
    padding: 12px;
    text-align: center;

  }

  .sample-btn {
    border: none;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 8px;
    cursor: default;
  }

  .sample-text {
    font-size: 12px;
  }
}

.sample-text {
  font-size: 12px;
  text-align: center;
}
/* Preview block LIGHT mode - force light styles even when global theme is dark */
.preview-block.light {
  background: #fff;
  border-color: #eee;

  .preview-top {
    color: #333 !important;

    .v-icon {
      color: #333 !important;
    }

    span {
      color: #333 !important;
    }
  }

  .mode-caption {
    color: #333 !important;
  }

  .mode-toggle .v-icon {
    color: #333 !important;
  }

  .sample {
    border-color: #eee !important;


  }
}

/* When global theme is dark, preview-block.light still needs light styles */
.theme--dark .preview-block.light {
  background: #fff;
  border-color: #eee;

  .preview-top {
    color: #333 !important;
    -webkit-text-fill-color: #333 !important;

    .v-icon {
      color: #333 !important;
      -webkit-text-fill-color: #333 !important;
    }

    span {
      color: #333 !important;
      -webkit-text-fill-color: #333 !important;
    }
  }

  .mode-caption {
    color: #333 !important;
    -webkit-text-fill-color: #333 !important;
  }

  .mode-toggle .v-icon {
    color: #333 !important;
    -webkit-text-fill-color: #333 !important;
  }

  .sample {
    border-color: #eee !important;

    .sample-content {

      &.light-preview {
        .sample-btn {
          color: var(--text-color, #333) !important;
          -webkit-text-fill-color: var(--text-color, #333) !important;
        }

        .sample-text {
          color: var(--text-color, #333) !important;
          -webkit-text-fill-color: var(--text-color, #333) !important;
        }
      }
    }

    .sample-header {
      color: var(--text-color, #fff) !important;
      -webkit-text-fill-color: var(--text-color, #fff) !important;
    }

    .sample-btn {
      color: var(--text-color, #fff) !important;
      -webkit-text-fill-color: var(--text-color, #fff) !important;
    }

    .sample-text {
      color: var(--text-color, #333) !important;
      -webkit-text-fill-color: var(--text-color, #333) !important;
    }
  }
}

/* Contrast indicator styles */
.contrast-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 20px 0 30px;

  .contrast-label {
    font-size: 14px;
    font-weight: 500;
  }

  .contrast-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .color-half.primary,  
  .color-half.secondary {
    background-color: var(--dynamic-bg) !important;
  }

  .color-circle {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    overflow: hidden;
    display: flex;

    .color-half {
      width: 50%;
      height: 100%;

      &.primary {
        border-radius: 12px 0 0 12px;
      }

      &.secondary {
        border-radius: 0 12px 12px 0;
      }
    }
  }

  .check-icon {
    width: 16px;
    font-size: 16px;

    &.met {
      color: #28ea96 !important;
    }

    &.not-met {
      color: #f44336 !important;
    }
  }
}

/* Dark mode preview contrast row */
.preview-block.dark .contrast-row {
  background: #2d3540;
  border-color: #424242;

  .contrast-label {
    color: rgba(255, 255, 255, 0.8);
  }
}

/* Light mode preview contrast row when global theme is dark */
.preview-block.light .contrast-row {
  background: #f5f5f5;
  border-color: #e0e0e0;

  .contrast-label {
    color: #666;
  }
}

.theme--dark .preview-block.light .contrast-row {
  background: #f5f5f5;
  border-color: #e0e0e0;

  .contrast-label {
    color: #666;
  }
}

.theme--light .tournament-branding {
  --disabled-bg: #f5f5f5;
  --disabled-border: #e0e0e0;
  --disabled-text: #9e9e9e;
  --disabled-icon: #9e9e9e;
}

.theme--dark .tournament-branding {
  --disabled-bg: rgba(255, 255, 255, 0.06);
  --disabled-border: rgba(255, 255, 255, 0.14);
  --disabled-text: rgba(255, 255, 255, 0.55);
  --disabled-icon: rgba(255, 255, 255, 0.45);
}

::v-deep .v-input--is-disabled .v-input__control .v-input__slot {
  background-color: var(--disabled-bg) !important;
  border-color: var(--disabled-border) !important;
}

::v-deep .v-input--is-disabled input,
::v-deep .v-input--is-disabled textarea {
  color: var(--disabled-text) !important;
  -webkit-text-fill-color: var(--disabled-text) !important;
}

::v-deep .v-input--is-disabled .v-label {
  color: var(--disabled-text) !important;
}

::v-deep .v-input--is-disabled .v-select__selection {
  color: var(--disabled-text) !important;
}

::v-deep .v-input--is-disabled .v-icon {
  color: var(--disabled-icon) !important;
}

::v-deep
  .v-input--is-disabled
  .v-input__details
  .v-messages
  .v-messages__message {
  color: var(--disabled-text) !important;
}

::v-deep .v-input--is-disabled .v-input__details .v-counter {
  color: var(--disabled-text) !important;
}

.upload-content.dragging {
  border-radius: 10px;
  outline: 2px solid var(--v-mainGreen-base);
  outline-offset: 4px;
}

.info-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  align-items: stretch;
  width: 100%;
}

.info-card {
  width: 100%;
}

.info-card--dark {
  background: #14161B;
}

.info-card--dark .section-title{
  color: #FFF !important;
}

.info-card--dark .icon-color{
  color: #FFF !important;
  height: 16px !important;
  width: 16px !important;
}

.info-card--dark .contrast-row .contrast-label{
  color: #FFF !important;
}

.info-card--dark  .title-logo{
  color: #FFF !important;
}

.info-card--dark .upload-dropzone .upload-box .upload-box__title{
  color: #FFF !important;
}
.info-card--dark .upload-dropzone .upload-box .background-icon .v-icon{
  color: #FFF !important;
}

.appearance-row {
  display: grid;
  grid-template-rows: 1fr 1fr;
}

.mode-card.dark .color-row:hover {
  background: rgba(255, 255, 255, 0.08);
}

.preview-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.buttons-row {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}

::v-deep .info-card--dark .section .section-title-row .theme--light.v-icon {
  color: white !important;
}


/*Card Image upload*/ 

.field-block {
  margin-bottom: 14px;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 2px;
  color: rgba(0, 0, 0, 0.75);
}

.upload-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.upload-box__left {
  display: flex;
  align-items: center;
  min-width: 0;
}

.background-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 55px;
  width: 55px;
  border-radius: 5px;
}

.upload-box__icon {
  font-size: 25px !important;
  opacity: 0.7;
}

.upload-box__text {
  min-width: 0;
}

.upload-box__title {
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 2px;
}

.upload-box__meta {
  font-size: 12px;
  color: #ABABAB;
  font-weight: 300;
}

.upload-box__btn {
  border-radius: 10px;
  text-transform: none;
  font-weight: 600;
}

.upload-box--has-image .upload-box__preview {
  width: 100%;
}

.upload-box__preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.upload-box__previewImg {
  max-width: 120px;
  max-height: 80px;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  background: #f5f5f5;
}

.upload-box__previewActions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.upload-box__removeBtn {
  min-width: auto;
  color: rgba(0, 0, 0, 0.6);
}

.upload-box__previewRow {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
}

.upload-box__fileInfo {
  min-width: 0;
  flex: 1;
}

.upload-box__fileName {
  font-size: 12px;
  font-weight: 600;
}

.upload-box__dot {
  opacity: 0.6;
}

.upload-box__changeLink {
  padding: 0;
  border: 0;
  background: transparent;
  color: #1a73e8; 
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  display: inline-flex;
  align-items: center;
  gap: 6px; 
}

.upload-box__trashBtn {
  flex: 0 0 auto;
  color: rgba(0, 0, 0, 0.45);
}

::v-deep .upload-box__trashBtn--dark .theme--light.v-icon  {
  flex: 0 0 auto;
  color: #ABABAB !important;
}

.logo-preview-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.logo-preview-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 200px; 
  height: 100px;
}

.logo-preview-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.section-divider {
  border: 0;
  height: 1px;
  background-color: rgba(0, 0, 0, 0.1);
  margin: 16px 0;
}

.info-card--dark .section-divider {
  background-color: rgba(255, 255, 255, 0.15);
}


</style>
